"use client";

import { CheckIcon } from "@heroicons/react/24/solid";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { PropsWithChildren, ReactNode } from "react";
import { Type } from "./constant";
import { classNames } from "@/helpers/tailwind-class";
import { trackEvent } from "@/shared/analytics-wrapper";
import { userVideoCdnBaseUrl } from "@/shared/constants";
import { GradientBorder } from "@/shared/gradient-border";

export function StepWrapper({ children, step }: PropsWithChildren<{ step?: number }>) {
  return (
    <div className="relative rounded-md bg-slate-800 shadow-md shadow-slate-800">
      {step && (
        <div className="absolute -top-3 left-2 size-6 rounded-md border border-slate-700 bg-slate-800 text-center align-middle text-base shadow-md">
          {step}
        </div>
      )}
      <div className="h-full w-full overflow-hidden rounded-md">{children}</div>
    </div>
  );
}

export function Benefits({
  showButton,
  type,
  mode = "Face Swaps",
}: {
  showButton: boolean;
  type: Type;
  mode?: "Face Swaps" | "Lip Syncs";
}) {
  const router = useRouter();
  return (
    <GradientBorder borderSize={2}>
      <div className="h-full w-full rounded-md bg-slate-900 p-4">
        <div className="flex items-center justify-between">
          <div className="font-bold">Upgrade to Premium {mode}</div>
          {showButton && (
            <Link href="/pricing" className="btn btn-primary btn-sm hidden lg:flex" target="_blank">
              Upgrade Now
            </Link>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 text-sm sm:grid-cols-2">
          {[
            "No daily limit",
            "No watermark",
            "Access to premium features",
            "Longer videos",
            "HD quality",
            "Larger file uploads (over 1GB)",
          ].map((item) => (
            <div key={item} className="flex items-center gap-2">
              <CheckIcon className="h-4 w-4 text-indigo-600" />
              <span>{item}</span>
            </div>
          ))}
        </div>

        {showButton && (
          <button
            className="btn btn-primary btn-sm mt-2 flex lg:hidden"
            onClick={() => {
              router.push("/pricing");
              trackEvent({
                eventName: "freeFaceSwapBenefits",
                action: "click",
                category: type,
              });
            }}
          >
            Get Unlimited Access
          </button>
        )}
      </div>
    </GradientBorder>
  );
}

export function FormWrapper({
  left,
  right,
  type,
}: {
  left: ReactNode;
  right: ReactNode;
  type: Type;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-4">
        <div className="">{left}</div>
        <div className="hidden md:block">
          <Benefits showButton={true} type={type} />
        </div>
      </div>

      <div>{right}</div>

      <div className="row-span-1 block md:hidden">
        <Benefits showButton={true} type={type} />
      </div>
    </div>
  );
}

export function ImagePresets({
  handleClick,
  selected,
}: {
  handleClick: (path: string) => void;
  selected: (string | undefined)[];
}) {
  const presets = [1, 2, 3, 4, 5, 6, 7, 8];

  return (
    <div className="flex flex-col gap-2 p-2">
      <p className="text-center text-xs">Or use a preset image</p>

      <div className={"grid grid-cols-4 gap-2"}>
        {presets.map((example) => {
          const path = `initial-images/presets/face-swap/preset${example}.jpg`;
          return (
            <button
              type="button"
              className={classNames(
                "aspect-square flex-1 rounded-md border",
                selected.includes(path) ? "border-info" : "border-slate-800/0"
              )}
              key={example}
              onClick={() => {
                trackEvent({ eventName: "freeFaceSwapImagePreset", action: "click" });
                handleClick(path);
              }}
            >
              <img
                src={`${userVideoCdnBaseUrl}/initial-images/presets/face-swap/preset${example}-thumbnail.jpg`}
                alt="Preset Image"
                className="h-full w-full rounded-md object-fill"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function isUrl(val: string | undefined) {
  if (!val) return false;
  try {
    new URL(val);
    return true;
  } catch {
    return false;
  }
}
